/**
 * Per-org "which app does this portal launch" choice, remembered in
 * localStorage (South32 launcher gap, 2026-09-21: an org with several
 * visible apps and none matching the minezero heuristics left the
 * launch button dead-ended on an error card).
 *
 * The choice is keyed by the WorkOS organization id so switching orgs
 * never launches another org's pick. A tiny external store (not bare
 * localStorage reads) so every launch surface — the header button AND
 * the join-flow hook — re-resolves the moment a pick is made, without
 * a page round-trip.
 */
import { useSyncExternalStore } from 'react';

const keyFor = (orgId: string) => `mz-launch-app-choice:${orgId}`;

const listeners = new Set<() => void>();

export function getStoredLaunchAppId(orgId: string | null | undefined): string | null {
  if (!orgId) return null;
  try {
    return localStorage.getItem(keyFor(orgId));
  } catch {
    return null; // storage disabled (private mode) — behave as "no choice"
  }
}

export function setStoredLaunchAppId(orgId: string | null | undefined, appId: string): void {
  if (!orgId) return;
  try {
    localStorage.setItem(keyFor(orgId), appId);
  } catch {
    /* storage disabled — the in-memory notify below still updates this tab */
  }
  for (const l of listeners) l();
}

/** Reactive read of the stored choice for the current org. */
export function useStoredLaunchAppId(orgId: string | null | undefined): string | null {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => getStoredLaunchAppId(orgId),
  );
}
